import { CommonRegistry } from './components';

export type CreateComponentUnion<TRegistry extends CommonRegistry> = {
  [K in keyof TRegistry]: {
    readonly typeName: K;
  } & TRegistry[K];
}[keyof TRegistry];

export type CreateComponentArgs<
  TRegistry extends CommonRegistry,
  TDefaults,
  K extends keyof TRegistry,
> = K extends keyof TDefaults ? Partial<TRegistry[K]> : TRegistry[K];

export type GetDefaults<TReg extends { readonly __defaults: object }> = TReg['__defaults'];

export function createComponentFactory<TRegistry extends CommonRegistry, TDefaults extends object>(
  defaults: TDefaults,
) {
  return function createComponent<K extends keyof TRegistry>(
    name: K,
    args: CreateComponentArgs<TRegistry, TDefaults, K>,
  ): { readonly typeName: K } & TRegistry[K] {
    if (name in defaults) {
      const d = (defaults as any)[name]; // eslint-disable-line @typescript-eslint/no-explicit-any
      const finalData = { ...d, ...(args as Partial<TRegistry[K]>) };

      return {
        typeName: name,
        ...finalData,
      } as unknown as { readonly typeName: K } & TRegistry[K];
    }

    return {
      typeName: name,
      ...(args as TRegistry[K]),
    } as unknown as { readonly typeName: K } & TRegistry[K];
  };
}

export function updateComponent<T extends { typeName: string }>(
  component: T,
  args: Partial<T>,
): void {
  Object.assign(component, args);
}
